import React, { createContext, useContext, useState, useEffect } from "react";

export type Theme = "light" | "dark" | "system";
export type Currency = "USD" | "EUR" | "BTC" | "ETH";
export type Language = "English" | "Spanish" | "French" | "German";

interface SettingsContextType {
  theme: Theme;
  currency: Currency;
  language: Language;
  avatar: string;
  setTheme: (theme: Theme) => void;
  setCurrency: (currency: Currency) => void;
  setLanguage: (language: Language) => void;
  setAvatar: (avatar: string) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined,
);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    console.error("useSettings must be used within a SettingsProvider");
    // Return default values instead of throwing an error
    return {
      theme: "system" as Theme,
      currency: "USD" as Currency,
      language: "English" as Language,
      avatar: "",
      setTheme: () => {},
      setCurrency: () => {},
      setLanguage: () => {},
      setAvatar: () => {},
    };
  }
  return context;
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>("system");
  const [currency, setCurrency] = useState<Currency>("USD");
  const [language, setLanguage] = useState<Language>("English");
  const [avatar, setAvatar] = useState<string>("");

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
    } else if (theme === "light") {
      root.classList.remove("dark");
    } else {
      // System theme
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      if (prefersDark) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }

      // Listen for system theme changes
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e: MediaQueryListEvent) => {
        if (theme === "system") {
          if (e.matches) {
            root.classList.add("dark");
          } else {
            root.classList.remove("dark");
          }
        }
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem("theme") as Theme;
      const savedCurrency = localStorage.getItem("currency") as Currency;
      const savedLanguage = localStorage.getItem("language") as Language;
      const savedAvatar = localStorage.getItem("avatar");

      if (savedTheme) setTheme(savedTheme);
      if (savedCurrency) setCurrency(savedCurrency);
      if (savedLanguage) setLanguage(savedLanguage);
      if (savedAvatar) setAvatar(savedAvatar);
    } catch (error) {
      console.error("Error loading settings from localStorage:", error);
    }
  }, []);

  // Save settings to localStorage when they change
  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    try {
      localStorage.setItem("theme", newTheme);
    } catch (error) {
      console.error("Error saving theme to localStorage:", error);
    }
  };

  const handleSetCurrency = (newCurrency: Currency) => {
    setCurrency(newCurrency);
    try {
      localStorage.setItem("currency", newCurrency);
    } catch (error) {
      console.error("Error saving currency to localStorage:", error);
    }
  };

  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    try {
      localStorage.setItem("language", newLanguage);
    } catch (error) {
      console.error("Error saving language to localStorage:", error);
    }
  };

  const handleSetAvatar = (newAvatar: string) => {
    setAvatar(newAvatar);
    try {
      localStorage.setItem("avatar", newAvatar);
    } catch (error) {
      console.error("Error saving avatar to localStorage:", error);
    }
  };

  const value = {
    theme,
    currency,
    language,
    avatar,
    setTheme: handleSetTheme,
    setCurrency: handleSetCurrency,
    setLanguage: handleSetLanguage,
    setAvatar: handleSetAvatar,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
